import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Paperclip, User } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

interface Message { from: string; text: string; agent_name?: string; created_at?: string; }

export default function LiveChat() {
  const { lang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [visitorName, setVisitorName] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [started, setStarted] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<any>(null);

  const greetings: Record<string, string> = {
    pt: 'Olá! Bem-vindo à Maluz Technologies. Como posso ajudar? Pode perguntar sobre licenças, suporte, preços ou qualquer serviço.',
    fr: 'Bonjour! Bienvenue chez Maluz Technologies. Comment puis-je vous aider?',
    en: 'Hello! Welcome to Maluz Technologies. How can I help you? Ask about licenses, support, pricing or any service.',
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages
  useEffect(() => {
    if (conversationId && open) {
      const poll = async () => {
        try {
          const { data } = await axios.get(`${API}/api/chat/messages/${conversationId}`);
          if (data.length > messages.length) {
            setMessages(data.map((m: any) => ({ from: m.from, text: m.text, agent_name: m.agent_name, created_at: m.created_at })));
          }
        } catch {}
      };
      pollRef.current = setInterval(poll, 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [conversationId, open, messages.length]);

  const handleOpen = () => {
    setOpen(true);
    if (!started) {
      setMessages([{ from: 'bot', text: greetings[lang] || greetings.pt }]);
    }
  };

  const handleStart = () => {
    if (!visitorName.trim()) return;
    setStarted(true);
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setMessages(prev => [...prev, { from: 'visitor', text: userMsg }]);
    setInput('');
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/chat/message`, {
        conversation_id: conversationId,
        message: userMsg,
        visitor_name: visitorName,
        visitor_email: visitorEmail,
      });
      if (data.conversation_id && !conversationId) setConversationId(data.conversation_id);
      if (data.bot_reply) {
        setTimeout(() => {
          setMessages(prev => [...prev, { from: 'bot', text: data.bot_reply }]);
        }, 800);
      }
    } catch {} finally { setSending(false); }
  };

  const handleFileAttach = () => fileInputRef.current?.click();
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const msg = `[Attached file: ${file.name} (${(file.size / 1024).toFixed(1)} KB)]`;
    setMessages(prev => [...prev, { from: 'visitor', text: msg }]);
    try {
      await axios.post(`${API}/api/chat/message`, {
        conversation_id: conversationId,
        message: msg,
        visitor_name: visitorName,
        visitor_email: visitorEmail,
      });
    } catch {}
    e.target.value = '';
  };

  return (
    <>
      {!open && (
        <button data-testid="live-chat-btn" onClick={handleOpen} style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 90,
          width: 56, height: 56, borderRadius: '50%',
          background: 'var(--primary)', color: 'white', border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(13,71,161,0.4)', cursor: 'pointer',
          transition: 'transform 0.2s',
        }}
          onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
          onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}>
          <MessageCircle size={24} />
        </button>
      )}

      {open && (
        <div data-testid="live-chat-window" style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 90,
          width: 380, height: 520, maxHeight: '75vh',
          background: 'white', border: '1px solid var(--border)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ padding: '14px 16px', background: 'var(--primary)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 15 }}>Live Chat</p>
              <p style={{ fontSize: 11, opacity: 0.8 }}>Maluz Technologies</p>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}><X size={18} /></button>
          </div>

          {!started ? (
            /* Name/email form */
            <div style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12 }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{t('chat.start')}</p>
              <input data-testid="chat-visitor-name" value={visitorName} onChange={e => setVisitorName(e.target.value)} placeholder={`${t('chat.your_name')} *`}
                style={{ padding: '10px 12px', border: '1px solid var(--border)', fontSize: 14, outline: 'none' }} />
              <input data-testid="chat-visitor-email" value={visitorEmail} onChange={e => setVisitorEmail(e.target.value)} placeholder={t('chat.email_optional')}
                style={{ padding: '10px 12px', border: '1px solid var(--border)', fontSize: 14, outline: 'none' }} />
              <button data-testid="chat-start-btn" onClick={handleStart} disabled={!visitorName.trim()}
                style={{ padding: '12px', background: 'var(--primary)', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', opacity: visitorName.trim() ? 1 : 0.5 }}>
                {t('chat.start_btn')}
              </button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {messages.map((msg, i) => (
                  <div key={i} style={{ alignSelf: msg.from === 'visitor' ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                    {msg.from === 'agent' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                        <User size={10} style={{ color: 'var(--primary)' }} />
                        <span style={{ fontSize: 10, color: 'var(--primary)', fontWeight: 600 }}>{msg.agent_name || 'Agent'}</span>
                      </div>
                    )}
                    {msg.from === 'bot' && (
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>Maluz Bot</span>
                    )}
                    <div style={{
                      padding: '10px 14px',
                      background: msg.from === 'visitor' ? 'var(--primary)' : msg.from === 'agent' ? '#E8F0FE' : 'var(--bg-section)',
                      color: msg.from === 'visitor' ? 'white' : 'var(--text-primary)',
                      fontSize: 14, lineHeight: 1.5,
                      borderRadius: msg.from === 'visitor' ? '12px 12px 0 12px' : '12px 12px 12px 0',
                      border: msg.from === 'agent' ? '1px solid rgba(13,71,161,0.2)' : 'none',
                    }}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: 6, alignItems: 'center' }}>
                <button data-testid="chat-attach-btn" onClick={handleFileAttach} style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-section)', border: '1px solid var(--border)', cursor: 'pointer', flexShrink: 0 }}>
                  <Paperclip size={16} style={{ color: 'var(--text-muted)' }} />
                </button>
                <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
                <input data-testid="chat-input" value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  placeholder={t('chat.type_message')}
                  style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--border)', fontSize: 14, outline: 'none' }} />
                <button data-testid="chat-send-btn" onClick={handleSend} disabled={sending}
                  style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--primary)', color: 'white', border: 'none', cursor: 'pointer', flexShrink: 0, opacity: sending ? 0.5 : 1 }}>
                  <Send size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
